import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { ReactComponent as Arrow } from "images/arrow-down_gray.svg";
import { ReactComponent as DotIcon } from "images/dots.svg";
import "./draggableList.scss";
import { cn } from "@bem-react/classname";

export type DraggableItem = {
  id: string;
  [key: string]: any;
};

export type DraggableListProps<T extends DraggableItem = DraggableItem> = {
  droppableId: string;
  title?: (isExpanded: boolean) => ReactNode;
  items: T[];
  renderTask: (task: T, isSelected: boolean) => ReactNode;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string, zip?: string) => void;
  /** When set, overrides default `selectedIds.has(task.id)` for row selection state. */
  getItemSelected?: (task: T, selectedIds: Set<string>) => boolean;
  /** When set, called instead of `onToggleSelect(task.id)` for row checkbox. */
  onToggleItem?: (task: T) => void;
  emptyMessage?: string;
  expandedAll?: boolean;
  onToggleSelectAll?: (checked: boolean) => void;
  expandVersion?: number;
  description?: (isExpanded: boolean) => ReactNode;
  /** When true, items are not draggable (no drag handle) */
  disableDrag?: boolean;
  /** When true, `emptyMessage` is shown only while the list is expanded (not when collapsed). */
  hideEmptyWhenCollapsed?: boolean;
  /** Row ids to highlight (e.g. map selection without checkbox). */
  highlightedIds?: Set<string>;
  /** When `version` changes, this droppable expands (e.g. map focused on driver). */
  mapSyncExpand?: { version: number };
  /** Fires when the section is expanded via header click (not when collapsing). */
  onSectionHeaderClick?: (droppableId: string) => void;
};

const DraggableList = <T extends DraggableItem>({
  droppableId,
  title,
  items,
  renderTask,
  selectedIds = new Set(),
  onToggleSelect,
  getItemSelected,
  onToggleItem,
  emptyMessage = "No items available",
  expandedAll = false,
  onToggleSelectAll,
  expandVersion,
  description,
  disableDrag = false,
  hideEmptyWhenCollapsed = false,
  highlightedIds,
  mapSyncExpand,
  onSectionHeaderClick,
}: DraggableListProps<T>) => {
  const allSelected =
    items.length > 0 &&
    items.every((item) =>
      getItemSelected ? getItemSelected(item, selectedIds) : selectedIds.has(item.id)
    );

  const [manualExpand, setManualExpand] = useState<boolean | null>(null);
  const lastMapSyncVersion = useRef<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (expandVersion === undefined) return;
    setManualExpand(null);
  }, [expandVersion]);

  const mapSyncVersion = mapSyncExpand?.version;
  useLayoutEffect(() => {
    if (mapSyncVersion === undefined) return;
    if (lastMapSyncVersion.current === mapSyncVersion) return;
    lastMapSyncVersion.current = mapSyncVersion;
    setManualExpand(true);
  }, [mapSyncVersion]);

  const isExpanded = manualExpand !== null ? manualExpand : expandedAll;
  const descriptionContent = description?.(isExpanded);

  const handleToggleExpand = () => {
    setManualExpand((prev) => {
      const current = prev !== null ? prev : expandedAll;
      return !current;
    });
  };

  return (
    <Droppable droppableId={droppableId}>
      {(provided: any) => (
        <div
          className={cn("xgs-draggable-list")({ expanded: isExpanded ? "open" : "close" })}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          <div className="xgs-draggable-list__header">
            {handleToggleExpand && (
              <div
                className="xgs-draggable-list__title"
                onClick={() => {
                  const willExpand = !isExpanded;
                  handleToggleExpand();
                  if (willExpand) {
                    onSectionHeaderClick?.(droppableId);
                  }
                }}
              >
                <div className="xgs-draggable-list__title-stack">
                  <div className="xgs-draggable-list_title_container">
                    <Arrow
                      className="xgs-draggable-list__title-chevron"
                      width={8}
                      height={12}
                      color="#828282"
                      style={{
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                    {title && title(isExpanded)}
                  </div>
                  {descriptionContent != null ? (
                    <div className="xgs-draggable-list__title-content">{descriptionContent}</div>
                  ) : null}
                </div>
              </div>
            )}

            {onToggleSelectAll && items.length > 0 && (
              <label className="xgs-draggable-list__select-all">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(event) => onToggleSelectAll(event.target.checked)}
                />
              </label>
            )}
          </div>

          <div className={`xgs-draggable-list__content ${isExpanded ? "expanded" : ""}`}>
            {items.map((task, index) => {
              const isSelected = getItemSelected
                ? getItemSelected(task, selectedIds)
                : selectedIds.has(task.id);
              const isMapHighlighted = highlightedIds?.has(task.id) ?? false;
              return (
                <Draggable
                  key={task.id}
                  draggableId={`${droppableId}-${task.id}`}
                  index={index}
                  isDragDisabled={disableDrag}
                >
                  {(draggableProvided, snapshot) => (
                    <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      {...(disableDrag ? {} : draggableProvided.dragHandleProps)}
                      className={`xgs-draggable-list__item ${disableDrag ? "xgs-draggable-list__item--no-drag" : ""} ${isSelected ? "is-selected" : ""} ${isMapHighlighted ? "is-map-highlighted" : ""}`.trim()}
                      style={{
                        ...draggableProvided.draggableProps.style,
                        opacity: snapshot.isDragging ? 0.9 : 1,
                      }}
                    >
                      {!disableDrag && <DotIcon className="xgs-draggable-list__drag-icon" />}
                      {(onToggleSelect || onToggleItem) && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            onToggleItem ? onToggleItem(task) : onToggleSelect!(task.id, task.zip)
                          }
                          onClick={(event) => event.stopPropagation()}
                          className="xgs-draggable-list__checkbox"
                        />
                      )}
                      <div className="xgs-draggable-list__item-body">{renderTask(task, isSelected)}</div>
                    </div>
                  )}
                </Draggable>
              );
            })}
          </div>

          {!items.length && (!hideEmptyWhenCollapsed || isExpanded) && (
            <div className="xgs-draggable-list__empty">{emptyMessage}</div>
          )}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
};

export default DraggableList;